import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { createEntry, updateEntry } from "../../../redux/actions/blogActions";
import AuthValidator from "../../auth/AuthValidator";

import EntryForm from "./EntryForm";
import {
  EntryBasic,
  Entry3D,
  EntryPixel,
  EntryDev,
  EntryWrite,
} from "./layouts/EntryLayouts";
import { useFirebaseConnect } from "react-redux-firebase";
import CONSTANTS from "../../../misc/constants";
import styled from "styled-components";
import { useRef } from "react";


const EntryEditor = ({ action }) => {
  const { key } = useParams();

  const [entryType, setEntryType] = useState("basic");
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [featured, setFeatured] = useState(false);
  const [modelURL, setModelURL] = useState("");
  const [description, setDescription] = useState("");
  const [text, setText] = useState("");
  const [featuredImage, setFeaturedImage] = useState("");
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [tags, setTags] = useState([]);
  const [importantLinks, setImportantLinks] = useState([]);
  const [creationDate, setCreationDate] = useState();
  const [actualAction, setActualAction] = useState(action);
  const [lang, setLang] = useState("esp");
  const [prevLang, setPrevLang] = useState("esp");

  const previewRef = useRef();

  const states = {
    entryType,
    title,
    slug,
    featured,
    description,
    featuredImage,
    images,
    videos,
    tags,
    importantLinks,
    text,
    modelURL,
    creationDate,
    lang,
    prevLang,
  };

  const setStates = {
    setEntryType,
    setTitle,
    setSlug,
    setFeatured,
    setDescription,
    setFeaturedImage,
    setImages,
    setVideos,
    setTags,
    setImportantLinks,
    setText,
    setModelURL,
    setCreationDate,
    setLang,
    setPrevLang,
  };

  const uid = useSelector((store) => store.firebase.auth.uid);
  const dispatch = useDispatch();

  /**<-- Get Entry Data from firebase in edit mode */
  useFirebaseConnect([
    {
      type: "once",
      path: "blog_entries",
      queryParams: ["orderByKey", `equalTo=${key}`],
    },
  ]);

  const blog_entry = useSelector(
    (store) => store.firebase.ordered.blog_entries
  );
  /**Get Entry Data from firebase in edit mode -->*/

  /**<-- Set the title based on actual action edit or create */
  const getTitle = useCallback(() => {
    switch (action) {
      case "create":
        return "New Entry";
      case "edit":
        return `Editing ${title}`;
      default:
        return "";
    }
  }, [action, title]);

  useEffect(() => {
    document.title = `${CONSTANTS.APP_NAME} | ${getTitle()}`;
  }, [getTitle]);
  /**Set the title based on actual action edit or create -->*/

  /**<-- Get all the needed values to create or update a entry */
  const getEntry = () => {
    const tagsObj = {};
    tags.forEach((tag) => {
      tagsObj[tag.replace(" ", "_")] = true;
    });

    let entry = {
      content: {
        [lang]: {
          title,
          description,
          featured_image: featuredImage !== "" ? featuredImage : null,
          images,
          videos,
          important_links: importantLinks,
          /******** 3D Field */
          model_URL: modelURL !== "" ? modelURL : null,
          /** Writing Field */
          text: entryType === CONSTANTS.ENTRY_TYPES.WRITING ? text || "" : null,
        },
      },
      meta: {
        uid,
        creationDate,
        updateDate: 0 - Date.now(),
        tags: tagsObj,
        featured,
      },
      entryType,
      slug,
    };

    if (action === "edit" && blog_entry) {
      if (lang === "esp" && blog_entry[0].value.content.en) {
        entry.content.en = { ...blog_entry[0].value.content.en };
      } else if (lang === "en" && blog_entry[0].value.content.esp) {
        entry.content.esp = { ...blog_entry[0].value.content.esp };
      }
    }

    if (lang === "esp" && entry.content.esp) {
      const newSearchValue = entry.content.esp.title
        .replace(/[ ]/g, "_")
        .toLowerCase();
      if (blog_entry && !blog_entry[0].value.search_en) {
        entry.search_en = newSearchValue;
      }
      entry.search_esp = newSearchValue;
    } else if (lang === "en" && entry.content.en) {
      const newSearchValue = entry.content.en.title
        .replace(/[ ]/g, "_")
        .toLowerCase();
      if (blog_entry && !blog_entry[0].value.search_esp) {
        entry.search_esp = newSearchValue;
      }
      entry.search_en = newSearchValue;
    }

    if (action === "create") {
      entry.meta.creationDate = 0 - Date.now();
    }

    return entry;
  };

  /** Get all the needed values to create or update a entry -->*/

  const handleSubmit = (event) => {
    event.preventDefault();
    const entry = getEntry();
    if (actualAction === "edit") {
      dispatch(updateEntry(entry, key));
    } else {
      dispatch(createEntry(entry));
      setActualAction("edit");
    }
    window.location.replace(`/entry/${entry.slug}`);
  };

  const hideToggle = () => {
    const element = previewRef.current;
    if (Object.values(element.classList).includes("hidden")) {
      element.classList.remove("hidden");
    } else {
      element.classList.add("hidden");
    }
  };

  let PreviewComponent = EntryBasic;
  switch (entryType) {
    case CONSTANTS.ENTRY_TYPES.MODELING_3D: {
      PreviewComponent = Entry3D;
      break;
    }
    case CONSTANTS.ENTRY_TYPES.PIXEL_ART: {
      PreviewComponent = EntryPixel;
      break;
    }
    case CONSTANTS.ENTRY_TYPES.DEVELOPMENT: {
      PreviewComponent = EntryDev;
      break;
    }
    case CONSTANTS.ENTRY_TYPES.WRITING: {
      PreviewComponent = EntryWrite;
      break;
    }
    default: {
      break;
    }
  }

  return (
    <AuthValidator permission_level={5}>
      <EntryEditorStyled>
        <div className="editor-head">
          {action && <h1>{getTitle()}</h1>}{" "}
          <button className="action-btn" onClick={hideToggle}>
            <i className="far fa-eye"></i>
          </button>
        </div>

        <div>
          <EntryForm
            onSubmit={handleSubmit}
            states={states}
            setStates={setStates}
            action={action}
            initialValues={blog_entry}
          />
        </div>
        <div className="preview hidden" ref={previewRef}>
          <PreviewComponent
            data={{ ...getEntry(), content: { ...getEntry().content[lang] } }}
          />
        </div>
      </EntryEditorStyled>
    </AuthValidator>
  );
};

const EntryEditorStyled = styled.div`
  .editor-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .hidden {
    display: none;
  }
  .preview {
    position: absolute;
    background-color: var(--white);
    min-height: 100%;
    top: 16rem;
    right: 0;
    left: 0;
    bottom: auto;
    padding: 0 1rem 2rem 1rem;
    border-bottom: solid 1rem var(--mainColor);
    @media (min-width: 992px) {
      top: 10rem;
      right: 0;
      left: 8rem;
      padding: 0 2rem;
    }
  }
`;

export default EntryEditor;
